import React from 'react';
import {createPurchase, setupStore} from 'components/checkout/setup';
import FAQ from 'components/FAQ';
import LoadingSpinner from 'components/LoadingSpinner';
import {PageHead} from 'components/PageHead';
import {usePrices} from 'components/utils/usePrices';
import Wrapper, {PageStyle} from 'components/Wrapper';
import {Routes} from 'constants/index';

import PricingCard from './components/PricingCard';

import * as styles from './styles.module.scss';

export default function Pricing() {
	const {isLoading, hasError, id, memberships, oneTime} = usePrices();

	if (isLoading) {
		return (
			<Wrapper style={PageStyle.SaladPage}>
				<div className={styles.wrapper}>
					<div className={styles.heading}>
						<h1>Simple pricing</h1>
						<span>No extra charges. No hidden fees.</span>
					</div>
					<div className={styles.loadingContainer}>
						<LoadingSpinner />
						<p>Loading pricing information...</p>
					</div>
				</div>
			</Wrapper>
		);
	}

	if (hasError) {
		return (
			<Wrapper style={PageStyle.SaladPage}>
				<div className={styles.wrapper}>
					<div className={styles.heading}>
						<h1>Simple pricing</h1>
						<span>No extra charges. No hidden fees.</span>
					</div>
					<div className={styles.errorContainer}>
						<p>Sorry, we couldn't load the pricing information. Please refresh the page.</p>
					</div>
				</div>
			</Wrapper>
		);
	}

	if (!memberships ||
		!memberships.MONTH ||
		!memberships.QUARTER ||
		!id ||
		!oneTime) {
		return (
			<Wrapper style={PageStyle.SaladPage}>
				<div className={styles.wrapper}>
					<div className={styles.heading}>
						<h1>Simple pricing</h1>
						<span>No extra charges. No hidden fees.</span>
					</div>
					<div className={styles.errorContainer}>
						<p>Incomplete pricing information. Please refresh the page.</p>
					</div>
				</div>
			</Wrapper>
		);
	}

	const MONTH = memberships.MONTH;
	const QUARTER = memberships.QUARTER;
	const productId = id;

	const quarterlyListItems = [
		'Great for maintenance routines',
		'Cancel anytime, no commitment',
		'Keep any unused scan credits',
		'The ultimate accountability tool',
		'Stay committed to long-term health',
		'Don\'t fall off the wagon',
		'Track changes over the long haul',
	];

	const monthlyListItems = [
		'Great for dedicated change making',
		'Cancel anytime, no commitment',
		'Keep any unused scan credits',
		'The ultimate accountability tool',
		'New diet or workout? See if it works',
		'Big event? Use us in the lead up',
		'See change (or lack thereof) early',
	];

	const lifetimeListItems = [
		'Don\'t know if you\'ll love it? Try first!',
		'Same scanner, service, and accuracy',
		'See the power of your data firsthand',
		'Get a 2-pack to share or for your follow-up',
	];

	return (
		<Wrapper style={PageStyle.SaladPage}>
			<div className={styles.wrapper}>
				<div className={styles.heading}>
					<h1>Simple pricing</h1>
					<span>No extra charges. No hidden fees.</span>
				</div>
				<div className={styles.row}>
					<PricingCard
						title='Monthly'
						description='Track progress like a pro: Set your fitness baseline and get insightful updates regularly.'
						prices={{
							button: 'Buy & save',
							single: {
								price: MONTH[0].price.total.toFixed(2),
								text: '1 scan / month, paid monthly',
								onClick: () => {
									setupStore(createPurchase({
										product: productId,
										interval: MONTH[0].renewInterval,
										quantity: MONTH[0].price.quantity,
										redirect: Routes.dashboard,
									}));
								},
							},
							double: {
								price: MONTH[1].price.total.toFixed(2),
								text: '2 scans / month, paid monthly',
								onClick: () => {
									setupStore(createPurchase({
										product: productId,
										interval: MONTH[1].renewInterval,
										quantity: MONTH[1].price.quantity,
										redirect: Routes.dashboard,
									}));
								},
							},
						}}
						listItems={monthlyListItems}
						highlight='Most Savings'
					/>
					<PricingCard
						title='Quarterly'
						description='The perfect plan for tracking your body composition changes over longer periods of time.'
						prices={{
							button: 'Buy & save',
							single: {
								price: QUARTER[0].price.total.toFixed(2),
								text: '1 scan / quarter, paid quarterly',
								onClick: () => {
									setupStore(createPurchase({
										product: productId,
										interval: QUARTER[0].renewInterval,
										quantity: QUARTER[0].price.quantity,
										redirect: Routes.dashboard,
									}));
								},
							},
							double: {
								price: QUARTER[1].price.total.toFixed(2),
								text: '2 scans / quarter, paid quarterly',
								onClick: () => {
									setupStore(createPurchase({
										product: productId,
										interval: QUARTER[1].renewInterval,
										quantity: QUARTER[1].price.quantity,
										redirect: Routes.dashboard,
									}));
								},
							},
						}}
						listItems={quarterlyListItems}
						highlight='Most Popular'
						active
					/>
					<PricingCard
						title='One-time purchase'
						description="Not yet ready to commit? Come try us out! We've made scans more affordable than ever!"
						prices={{
							button: 'Buy now',
							single: {
								price: oneTime[0].toFixed(2),
								text: 'for 1 baseline scan',
								onClick: () => {
									setupStore(createPurchase({
										product: productId,
										quantity: 1,
										redirect: Routes.dashboard,
									}));
								},
							},
							double: {
								price: oneTime[1].toFixed(2),
								text: '2 scans, baseline and follow-up',
								onClick: () => {
									setupStore(createPurchase({
										product: productId,
										quantity: 2,
										redirect: Routes.dashboard,
									}));
								},
							},
						}}
						listItems={lifetimeListItems}
					/>
				</div>
				<FAQ.Spacer />
				<FAQ />
			</div>
		</Wrapper>
	);
}

export const ProductLdJson =
	<script type='application/ld+json'>
		{`
			{
				"@context": "https://schema.org",
				"@type": "Product",
				"name": "BodySpec DEXA Scan",
				"image": [
					"https://static.bodyspec.com/at/e7/e6/e7e6a60befc22c5f",
					"https://static.bodyspec.com/at/f3/9d/f39d401ae60f8d87",
					"https://static.bodyspec.com/at/33/28/3328ebd33556c5b0",
					"https://static.bodyspec.com/at/30/c4/30c474c85de4f793",
					"https://static.bodyspec.com/at/39/1a/391ab638e229089f",
					"https://static.bodyspec.com/at/63/26/63261bc521ba84bd",
					"https://static.bodyspec.com/at/b2/24/b224454235cb0246",
					"https://static.bodyspec.com/at/00/c1/00c1ff30c8d96636",
					"https://static.bodyspec.com/at/e3/2a/e32a0f3e97630026"
				],
				"brand": {
					"@type": "Brand",
					"name": "BodySpec"
				},
				"description": "Understand your body and track changes with a BodySpec full body DEXA scan!",
				"review": [
					{
						"@type": "Review",
						"author": {
							"@type": "Person",
							"name": "Xie Cyan"
						},
						"datePublished": "2023-09-25",
						"reviewBody": "I have been here for multiple times because of my diet plan. Wonderful place!!! Results come out really fast!",
						"reviewRating": {
							"@type": "Rating",
							"bestRating": "5",
							"ratingValue": "5",
							"worstRating": "1"
						}
					},
					{
						"@type": "Review",
						"author": {
							"@type": "Person",
							"name": "Erk Eyupgiller"
						},
						"datePublished": "2023-09-20",
						"reviewBody": "The staff is incredibly friendly and welcoming, making every visit a positive experience. The DEXA scans have become an essential tool in tracking my fitness progress, providing accurate and insightful results that keep me accountable on my journey. I highly recommend BodySpec to anyone looking for a reliable and effective way to monitor their fitness goals. Keep up the good work!",
						"reviewRating": {
							"@type": "Rating",
							"bestRating": "5",
							"ratingValue": "5",
							"worstRating": "1"
						}
					},
					{
						"@type": "Review",
						"author": {
							"@type": "Person",
							"name": "Maris Naylor"
						},
						"datePublished": "2023-09-18",
						"reviewBody": "I've been using BodySpec since ~2019 and it's been such a huge tool in my health journey. I've been able to track changes over the years based on different things I experiment with, and it's really given me peace of mind to know that I can always see what's going on outside of just looking at my scale. Weight is just a measure of gravity. BodySpec helps me see things that actually matter. Booking is easy because of the many truck locations. Recommend buying a package. It's the best deal long term so you can see your progress.",
						"reviewRating": {
							"@type": "Rating",
							"bestRating": "5",
							"ratingValue": "5",
							"worstRating": "1"
						}
					},
					{
						"@type": "Review",
						"author": {
							"@type": "Person",
							"name": "Robert Edwards"
						},
						"datePublished": "2023-06-25",
						"reviewBody": "Super easy and professionally setup. The guy there was helpful and informative about the process. In and out in 15mins max, had the report emailed within the hour. Planning on using my one free consulting session to understand my report better. I’ll probably get a membership, as it’s important to track your make up over time.",
						"reviewRating": {
							"@type": "Rating",
							"bestRating": "5",
							"ratingValue": "5",
							"worstRating": "1"
						}
					}
				],
				"aggregateRating": {
					"@type": "AggregateRating",
					"ratingValue": "4.9",
					"reviewCount": 162
				}
			}
		`}
	</script>;

export const Head = PageHead(() => ({
	title: 'Pricing',
	custom: ProductLdJson,
}));
